import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useFilters } from "../context/FilterContext";
import { ChevronDown, SlidersHorizontal } from "lucide-react";

const SORTS = [
  { v: "catalogue", label: "Catalogue" },
  { v: "newest", label: "Newest" },
  { v: "price_asc", label: "Price · Low to High" },
  { v: "price_desc", label: "Price · High to Low" },
  { v: "popularity", label: "Most Loved" },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    open, setOpen,
    category, setCategory,
    collection, setCollection,
    sort, setSort,
    buildParams,
  } = useFilters();

  // Sync URL params on mount
  useEffect(() => {
    const cat = searchParams.get("category");
    const coll = searchParams.get("collection");
    if (cat && cat !== category) setCategory(cat);
    if (coll && coll !== collection) setCollection(coll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProducts(buildParams())
      .then(setProducts)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(buildParams())]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (category !== "All") next.set("category", category);
    if (collection !== "All") next.set("collection", collection);
    setSearchParams(next, { replace: true });
  }, [category, collection, setSearchParams]);

  const headline = useMemo(() => {
    if (category !== "All") return category;
    if (collection !== "All") return collection;
    return "The Catalogue";
  }, [category, collection]);

  return (
    <main data-testid="shop-page" className="bg-[#0A0A0A] pt-[68px] min-h-screen">
      <section className="border-b border-[#262626] py-12 md:py-20 relative grain">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-3">
            ✦ Browse
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
            {headline}.
          </h1>
        </div>
      </section>

      <div
        className="sticky top-[68px] z-20 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#262626]"
      >
        <div
          className="px-6 md:px-10 py-4 flex items-center justify-between gap-3 transition-all duration-500"
          style={{ marginLeft: open ? "300px" : 0 }}
        >
          <button
            onClick={() => setOpen(!open)}
            data-testid="toggle-filter-btn"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border border-[#262626] hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2.5 transition-colors"
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} />
            {open ? "Hide Filters" : "Show Filters"}
          </button>

          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none bg-transparent border border-[#262626] text-[#F5F5F5] text-[11px] tracking-[0.2em] uppercase pl-4 pr-9 py-2.5 hover:border-[#D4AF37] focus:border-[#D4AF37] outline-none cursor-pointer"
              >
                {SORTS.map((s) => (
                  <option key={s.v} value={s.v} className="bg-[#0A0A0A]">{s.label}</option>
                ))}
              </select>
              <ChevronDown size={14} strokeWidth={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#A3A3A3]" />
            </div>
          </div>
        </div>
      </div>

      <section className="transition-all duration-500" style={{ marginLeft: open ? "300px" : 0 }}>
        <div className="max-w-[1500px] px-6 md:px-10 pb-32 pt-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-[#141414] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center">
              <div className="font-serif-display italic text-3xl text-[#A3A3A3]">
                No pieces match your filters.
              </div>
            </div>
          ) : (
            <div className={`grid grid-cols-2 md:grid-cols-3 ${open ? "lg:grid-cols-3 xl:grid-cols-4" : "lg:grid-cols-4 xl:grid-cols-5"} gap-x-6 gap-y-12`}>
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
